import { Color } from '@persona-id/design-tokens';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const DragLayer = styled('div')`
  position: fixed;
  pointer-events: none;
  z-index: 100;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const SelectBox = styled('div')<{ left: number; top: number; width: number; height: number }>`
  border: 1px solid ${Color.PrimaryPurple};
  position: absolute;
  background-color: rgb(99, 31, 255, 0.16);
  left: ${({ left }) => left}px;
  top: ${({ top }) => top}px;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
`;

export const calculateIsOverlap = (boxA: SelectionBox, boxB: SelectionBox) => {
  return (
    boxB.left <= boxA.left + boxA.width &&
    boxB.left + boxB.width >= boxA.left &&
    boxB.top <= boxA.top + boxA.height &&
    boxB.top + boxB.height >= boxA.top
  );
};
export interface SelectionBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface DragSelectProps {
  handleOnMouseUp?: (selection: SelectionBox) => void;
  handleOnMouseMove?: (selection: SelectionBox) => void;
}

const DragSelect = ({ handleOnMouseUp, handleOnMouseMove }: DragSelectProps) => {
  const [startPosition, setStartPosition] = useState({ x: null, y: null });
  const [endPosition, setEndPosition] = useState({ x: null, y: null });
  // Using ref to store startPosition and endPosition to ensure most updated data
  // even when accessing from event listeners.
  const selectionBoxRef = useRef({ startPosition, endPosition });
  const [isDragging, setIsDragging] = useState(false);

  const calculateSelectBox = useCallback(() => {
    const { startPosition, endPosition } = selectionBoxRef.current;
    if (!(startPosition.x && startPosition.y && endPosition.x && endPosition.y)) {
      return {
        left: null,
        top: null,
        width: null,
        height: null,
      };
    }

    const left = Math.min(startPosition.x, endPosition.x);
    const top = Math.min(startPosition.y, endPosition.y);
    const width = Math.abs(startPosition.x - endPosition.x);
    const height = Math.abs(startPosition.y - endPosition.y);
    return {
      left,
      top,
      width,
      height,
    };
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      setIsDragging(true);
      setEndPosition({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        x: e.clientX,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        y: e.clientY,
      });
      selectionBoxRef.current.endPosition = {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        x: e.clientX,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        y: e.clientY,
      };
      e.preventDefault();
      e.stopPropagation();

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleOnMouseMove && handleOnMouseMove(calculateSelectBox());
    };

    const onMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      e.preventDefault();
      e.stopPropagation();

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleOnMouseUp && handleOnMouseUp(calculateSelectBox());
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!e.shiftKey) {
        return;
      }

      setEndPosition({ x: null, y: null });
      setStartPosition({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        x: e.clientX,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        y: e.clientY,
      });
      selectionBoxRef.current = {
        endPosition: { x: null, y: null },
        startPosition: {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          x: e.clientX,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          y: e.clientY,
        },
      };
      e.preventDefault();
      e.stopPropagation();

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousedown', onMouseDown);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [calculateSelectBox, handleOnMouseUp, handleOnMouseMove, startPosition, endPosition]);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return <DragLayer>{isDragging && <SelectBox {...calculateSelectBox()} />}</DragLayer>;
};

export default DragSelect;
